/**
 * skyship_interior.ts — the sky-ship's walkable five-level interior (pure
 * geometry builder; the SkyShipModule owns lifecycle, physics registration,
 * crew and prompts).
 *
 * Deck program (capital-ship research: bridge arc + officer country +
 * mess/berthing amidships + engineering at the keel; the foredeck is the
 * ship's promenade — HUGE, forward, open to the sky):
 *
 *   y=10  CONCOURSE        z −300..−40 (the hangar hall: airlock + return
 *                                       console aft, cargo, lifeboats)
 *   y=18  D1 BRIDGE        z +6..+30   (command arc, bow viewport)
 *   y=14  D2 OFFICERS+MED  z −30..+14  (captain's cabin, cabins, medbay)
 *   y=10  D3 CREW          z −40..+30  (mess, galley, berthing, lounge;
 *                                       aft doors → concourse, bow doors →)
 *   y=6   D4 ENGINEERING   z −40..+26  (reactor aft, cargo hold forward)
 *   y=10  SPINE GALLERY    z +30..+385 (windowed walk to the prow)
 *   y=10  PROW FOREDECK    z +385..+645 (taper 60 m → 24 m; last 60 m past
 *                                        the nose)
 *
 * All of it sits INSIDE the civa_city_hall hull (see HULL below).
 *
 * Frame: ship-local, +Z = bow, +X = starboard, y above ship origin. The
 * module composes with the ship's world position + yaw. All colliders are
 * returned as local boxes; materials are injected (textures stream from the
 * generated `textures/ship_*.png` tiles with flat fallbacks).
 */
import * as THREE from 'three/webgpu';

export const SHIP_ALT_M = 760;          // hull rides this high above the pad
export const FLOOR_T = 0.4;
export const WALL_T = 0.35;
export const CLEAR_H = 3.6;
/** A hung ceiling sheet under the deck above: this thick, this far below the slab's bottom (never coplanar). */
export const CEILING_T = 0.08;
export const CEILING_GAP = 0.01;
/** Floor inlays (the gold lines, the airlock threshold) float this far above the deck — below it they z-fight the slab at grazing angles. */
export const INLAY_LIFT = 0.035;

/**
 * THE HULL ENVELOPE — civa_city_hall (the user's Ostorch giant), measured
 * 72.8×21.2×90 m raw, flown at fixedScale 13: 946 m wide, 276 m tall,
 * 1170 m long, long axis Z. The interior is laid along that axis and stays
 * INSIDE it (the prow deck alone cantilevers past the nose). The interior's
 * ship-local origin sits at 22% of hull height (CommonsSettlements
 * deckBaseY), so every level below is well inside the mesh.
 */
export const HULL = { w: 946, h: 276, l: 1170 } as const;

export const DECKS = {
  engineering: { y: 6, z0: -40, z1: 26, hw: 13 },
  crew: { y: 10, z0: -40, z1: 30, hw: 13 },
  officers: { y: 14, z0: -30, z1: 14, hw: 10 },
  bridge: { y: 18, z0: 6, z1: 30, hw: 8 },
} as const;
export type DeckKey = keyof typeof DECKS;

/** The ARRIVAL CONCOURSE — the hangar hall aft of the room stack (crew level). */
export const CONCOURSE = { y: 10, z0: -300, z1: -40, hw: 45, clearH: 26 };
/** The SPINE GALLERY — the windowed walk from the bow doors to the prow. */
export const GALLERY = { y: 10, z0: 30, z1: 385, hw: 7 };
/** The PROW FOREDECK — 260 m of open deck, the last 60 m past the nose. */
export const FOREDECK = { y: 10, z0: 385, z1: 645, hw0: 30, hw1: 12 };

/** Airlock arrival point (boarding lands here) + the return console. */
export const ARRIVAL = { x: 0, y: CONCOURSE.y, z: -288 };
export const RETURN_CONSOLE = { x: -4.2, y: CONCOURSE.y, z: -292 };

/**
 * THE RIDE HANDOFF (field report 2026-09-02: "I fall through the ground of
 * the ship … no matter what, also if I am in a vehicle when I transport up
 * there make sure I am not in my vehicle"). A hovercraft ride owns the
 * player's height against the TERRAIN (AtlasVehicleModule integrates
 * atlas.localY from the hover spring while AtlasAvatarModule parks the
 * capsule), so a lift taken in the saddle is undone on the very next fixed
 * tick — the craft, and you with it, sink through the decks to the ground.
 * The lift therefore steps you off the skimmer FIRST; a FLYING ship cannot
 * board at all (flight owns Y the same way and the pad prompt is XZ-only):
 * land it. Pure so the harness pins the policy.
 */
export type BoardingGate = 'lift' | 'dismount' | 'land-first';

/**
 * THE LIFT IS AUTHORITATIVE (field report 2026-09-03: "whenever I move in
 * the sky ship above the civ-A cities, I fall through the floor and go down
 * levels of the ship and then fall through the ship and fall to the city").
 * The lift used to move the capsule ALONE — the server row stayed on the
 * pad, and the reconcile (which compares the row with the client's position
 * whenever nothing is in flight) read the 288 m to the airlock as a teleport
 * and hard-snapped the capsule back over the row a tick later. At Ostorch
 * that column happens to be the crew deck (the standing probe held); a row
 * that trailed a hovercraft, or a hull master off its pivot, put it outside
 * every floor. The `skyShipLift` reducer now moves the row with the lift
 * under this envelope (MIRROR of server/src/reducers/skyShip.ts — keep in
 * sync); the client holds reconcile while it is in flight.
 */
export const SKY_LIFT = {
  boardM: 60,        // the row must stand at the pad (BOARD_RANGE + a ride's row lag)
  reachM: 1600,       // a lift target lies within the hull's reach of the pad: the parked hull's ORIGIN is the model pivot (off the pad by up to ~750 m, the PIVOT LAW) + the airlock 288 m aft of it
  returnReachM: 2200, // the row may be anywhere aboard to return (origin offset + the prow at +645)
  returnLandM: 40,   // the return lands beside the shuttle pad (arrivalPoint)
  holdTicks: 600,    // reconcile hold while the reducer is in flight (10 s bound)
  settleTicks: 60,   // grace once it lands (the row arrives within a few ticks)
} as const;

/** The server's verdict, mirrored: may this lift move the row? */
export function liftEnvelopeOk(aboard: boolean, rowToPad: number, targetToPad: number, targetToLand: number): boolean {
  if (aboard) return rowToPad <= SKY_LIFT.boardM && targetToPad <= SKY_LIFT.reachM;
  return rowToPad <= SKY_LIFT.returnReachM && targetToLand <= SKY_LIFT.returnLandM;
}
export function boardingGate(hoverRide: boolean, skyFlight: boolean): BoardingGate {
  if (skyFlight) return 'land-first';
  return hoverRide ? 'dismount' : 'lift';
}

export interface ShipMats {
  deck: THREE.Material; wall: THREE.Material; ceiling: THREE.Material;
  bridgeFloor: THREE.Material; grate: THREE.Material; cargoFloor: THREE.Material;
  quartersWall: THREE.Material; hull: THREE.Material; foredeck: THREE.Material;
  glass: THREE.Material; glow: THREE.Material; gold: THREE.Material; dark: THREE.Material;
}

export interface LocalBox { x: number; y: number; z: number; hx: number; hy: number; hz: number; yaw: number }

export interface ShipInterior {
  group: THREE.Group;            // ship-local geometry (module positions it)
  colliders: LocalBox[];         // ship-local collision boxes
  glowMeshes: THREE.Mesh[];      // light-strip planes (share the glow material)
}

/** Foredeck half-width at local z (linear taper to the bow point). */
export function foredeckHalfW(z: number): number {
  const t = Math.max(0, Math.min(1, (z - FOREDECK.z0) / (FOREDECK.z1 - FOREDECK.z0)));
  return FOREDECK.hw0 + (FOREDECK.hw1 - FOREDECK.hw0) * t;
}

/** Which deck a local Y stands on (nearest floor at/below eye height). */
export function deckAt(y: number): DeckKey | 'foredeck' | null {
  for (const [k, d] of Object.entries(DECKS) as Array<[DeckKey, typeof DECKS[DeckKey]]>) {
    if (y >= d.y - 0.8 && y < d.y + CLEAR_H + 0.9) {
      // crew level shares its Y with the foredeck — resolved by Z in clampShipLocal.
      return k;
    }
  }
  return null;
}

/**
 * Project a ship-local XZ into the legal walkable region for its Y — the
 * reconcile-clamp mirror (the server has no walls; its corrections are
 * advisory aboard). Returns the clamped point + whether it moved.
 */
export function clampShipLocal(lx: number, lz: number, ly: number): { x: number; z: number; clamped: boolean } {
  interface Rect { x0: number; x1: number; z0: number; z1: number }
  const rects: Rect[] = [];
  const pad = 0.55;
  for (const d of Object.values(DECKS)) {
    if (ly >= d.y - 0.8 && ly < d.y + CLEAR_H + 0.9) {
      rects.push({ x0: -d.hw + pad, x1: d.hw - pad, z0: d.z0 + pad, z1: d.z1 - pad });
    }
  }
  // Concourse + gallery share the crew level.
  if (ly >= CONCOURSE.y - 0.8 && ly < CONCOURSE.y + CONCOURSE.clearH) {
    rects.push({ x0: -CONCOURSE.hw + pad, x1: CONCOURSE.hw - pad, z0: CONCOURSE.z0 + pad, z1: CONCOURSE.z1 + 0.5 });
  }
  if (ly >= GALLERY.y - 0.8 && ly < GALLERY.y + CLEAR_H + 0.9) {
    rects.push({ x0: -GALLERY.hw + pad, x1: GALLERY.hw - pad, z0: GALLERY.z0 - 0.5, z1: GALLERY.z1 + 0.5 });
  }
  // The foredeck (crew level, forward of the bow doors).
  if (ly >= FOREDECK.y - 0.8 && ly < FOREDECK.y + 9) {
    const hw = Math.max(1, foredeckHalfW(lz) - pad);
    rects.push({ x0: -hw, x1: hw, z0: FOREDECK.z0 - 0.5, z1: FOREDECK.z1 - pad });
  }
  if (rects.length === 0) return { x: lx, z: lz, clamped: false }; // between decks (stairs) — leave it
  let best: { x: number; z: number; d: number } | null = null;
  for (const r of rects) {
    const cx = Math.max(r.x0, Math.min(r.x1, lx));
    const cz = Math.max(r.z0, Math.min(r.z1, lz));
    const d = Math.hypot(cx - lx, cz - lz);
    if (!best || d < best.d) best = { x: cx, z: cz, d };
  }
  return { x: best!.x, z: best!.z, clamped: best!.d > 0.01 };
}

// ── The prop program: every generated GLB, placed with purpose ─────────────
// (key → placements; targetH = real-world silhouette the module fit-scales
// to; solid pieces get a walk collider box.)

export interface PropSpot { key: string; x: number; y: number; z: number; yaw: number; targetH: number; solid: boolean }

/**
 * THE DECOR READS SMALL (field report 2026-09-03: "the glb decor on the ship
 * are too small"). The targetH silhouettes are real-world honest, but in
 * 3.6 m-clear rooms 26 m across — and a 90 × 260 m hangar hall — honest
 * human-scale props vanish. Every placement fit-scales to
 * DECOR_SCALE × targetH, clamped to its room's clearance minus headroom so
 * nothing pokes through a ceiling (the hall and the open prow are unclamped
 * in practice). Pure: the harness pins the growth and the clamp.
 */
export const DECOR_SCALE = 1.45;
export const DECOR_HEADROOM = 0.35;
/** Vertical clearance over a spot: the room stack's CLEAR_H, the hangar hall's clearH, the open sky over the prow. */
export function clearanceAt(y: number, z: number): number {
  if (y === CONCOURSE.y && z < CONCOURSE.z1) return CONCOURSE.clearH;
  if (y === FOREDECK.y && z >= FOREDECK.z0) return 40;
  return CLEAR_H;
}
export function propTargetH(spot: { y: number; z: number; targetH: number }): number {
  return Math.min(spot.targetH * DECOR_SCALE, clearanceAt(spot.y, spot.z) - DECOR_HEADROOM);
}

const P = (key: string, x: number, y: number, z: number, yaw: number, targetH: number, solid = true): PropSpot =>
  ({ key: `prop:${key}`, x, y, z, yaw, targetH, solid });

export const SHIP_PROP_SPOTS: PropSpot[] = [
  // ── D1 Bridge (y18): the command arc faces the bow viewport. ──
  P('ship_captain_chair', 0, 18, 14.5, 0, 1.9),
  P('ship_helm_console', 0, 18, 23, Math.PI, 1.3),
  P('ship_console_bank', -4.6, 18, 21.5, Math.PI - 0.5, 1.2),
  P('ship_console_bank', 4.6, 18, 21.5, Math.PI + 0.5, 1.2),
  P('ship_holotable', 0, 18, 9.5, 0, 1.15),
  P('ship_screen_wall', -7.2, 18, 12, Math.PI / 2, 2.2),
  P('ship_nav_globe', 6.4, 18, 9, -0.7, 1.6),
  // ── D2 Officers + medbay (y14). ──
  P('ship_captain_desk', -6.5, 14, 10, Math.PI / 2, 1.15),
  P('ship_officer_bed', -8, 14, 6, Math.PI / 2, 0.85),
  P('ship_officer_bed', -8, 14, -2, Math.PI / 2, 0.85),
  P('ship_officer_bed', -8, 14, -10, Math.PI / 2, 0.85),
  P('ship_locker', -4, 14, -14.5, Math.PI, 2.1),
  P('ship_medbed', 6.5, 14, -6, -Math.PI / 2, 1.05),
  P('ship_medbed', 6.5, 14, -11, -Math.PI / 2, 1.05),
  P('ship_med_cabinet', 8.6, 14, -16, -Math.PI / 2, 1.9),
  P('ship_locker', 8.6, 14, 2, -Math.PI / 2, 2.1),
  P('ship_nav_globe', -3, 14, 12.4, 0.4, 1.5),
  // ── D3 Crew deck (y10): mess port-mid, galley aft of it, berthing
  //    starboard, lounge forward (the concourse opens through the aft doors). ──
  P('ship_mess_table', -7, 10, 2, Math.PI / 2, 0.8),
  P('ship_mess_table', -7, 10, 8, Math.PI / 2, 0.8),
  P('ship_galley', -10.5, 10, 6, Math.PI / 2, 1.5),
  P('ship_food_dispenser', -11.5, 10, -3, Math.PI / 2, 1.9),
  P('ship_bunk_triple', 8, 10, -4, -Math.PI / 2, 2.4),
  P('ship_bunk_triple', 8, 10, -9, -Math.PI / 2, 2.4),
  P('ship_bunk_triple', 8, 10, -14, -Math.PI / 2, 2.4),
  P('ship_locker', 11.2, 10, -18, -Math.PI / 2, 2.1),
  P('ship_locker', 4.6, 10, -18, Math.PI, 2.1),
  P('ship_lounge_sofa', -5, 10, 22, 0.7, 0.95),
  P('ship_lounge_sofa', 5, 10, 22, -0.7, 0.95),
  P('ship_gametable', 0, 10, 24.5, 0, 0.95),
  P('ship_screen_wall', -12.2, 10, 18, Math.PI / 2, 2.0),
  // ── D4 Engineering aft + cargo forward (y6). ──
  P('ship_reactor', 0, 6, -28, 0, 3.1),
  P('ship_eng_console', -6, 6, -22, 2.4, 1.25),
  P('ship_eng_console', 6, 6, -22, -2.4, 1.25),
  P('ship_pipe_wall', -12.2, 6, -30, Math.PI / 2, 2.6),
  P('ship_pipe_wall', 12.2, 6, -26, -Math.PI / 2, 2.6),
  P('ship_workbench', -9, 6, -14, Math.PI / 2, 1.1),
  P('ship_coolant_tank', 10, 6, -34, 0, 2.4),
  P('ship_cargo_crate', -6, 6, 8, 0.2, 1.3),
  P('ship_cargo_crate', -8.5, 6, 12, -0.3, 1.3),
  P('ship_cargo_crate', -5.5, 6, 15, 0.9, 1.3),
  P('ship_barrel_stack', 7, 6, 10, 0, 1.1),
  P('ship_barrel_stack', 9.5, 6, 14, 1.2, 1.1),
  P('ship_loader', 3, 6, 18, -0.6, 1.4),
  P('ship_cargo_crate', 8, 6, 20, 0.1, 1.3),
  // ── The ARRIVAL CONCOURSE (y10, z −300..−40): the hangar hall. ──
  P('ship_eng_console', -4.2, 10, -292, 0, 1.25),   // the RETURN console at the airlock
  P('ship_screen_wall', 6, 10, -294, Math.PI, 2.2),
  P('ship_cargo_crate', -28, 10, -250, 0.3, 1.3),
  P('ship_cargo_crate', -32, 10, -244, -0.4, 1.3),
  P('ship_cargo_crate', -25, 10, -240, 0.9, 1.3),
  P('ship_cargo_crate', 30, 10, -200, 0.1, 1.3),
  P('ship_barrel_stack', 26, 10, -230, 0, 1.1),
  P('ship_barrel_stack', -30, 10, -180, 1.2, 1.1),
  P('ship_loader', 12, 10, -215, -0.6, 1.4),
  P('ship_loader', -14, 10, -150, 2.4, 1.4),
  P('ship_coolant_tank', 34, 10, -120, 0, 2.4),
  P('ship_pipe_wall', -43, 10, -100, Math.PI / 2, 2.6),
  P('ship_pipe_wall', 43, 10, -160, -Math.PI / 2, 2.6),
  P('ship_workbench', 30, 10, -80, -Math.PI / 2, 1.1),
  P('ship_lifeboat', -38, 10, -270, Math.PI / 2, 2.2),
  P('ship_lifeboat', 38, 10, -270, -Math.PI / 2, 2.2),
  P('ship_floodlight', -20, 10, -120, 0.4, 4.2),
  P('ship_floodlight', 20, 10, -60, -0.4, 4.2),
  P('station_crates', 20, 10, -180, 0.5, 1.6),
  P('station_gantry', 0, 10, -110, 0, 5.2),
  P('station_planter', -12, 10, -46, 0, 1.1),
  P('station_planter', 12, 10, -46, 0, 1.1),
  // ── The SPINE GALLERY (y10, z 30..385): benches + planters on the walk. ──
  P('station_bench', -5.5, 10, 70, Math.PI / 2, 0.9),
  P('station_bench', 5.5, 10, 140, -Math.PI / 2, 0.9),
  P('station_planter', -5.5, 10, 105, 0, 1.1),
  P('station_planter', 5.5, 10, 210, 0, 1.1),
  P('station_bench', -5.5, 10, 245, Math.PI / 2, 0.9),
  P('ship_screen_wall', 6.2, 10, 320, -Math.PI / 2, 2.0),
  P('station_bench', 5.5, 10, 350, -Math.PI / 2, 0.9),
  // ── The PROW FOREDECK (y10, z 385..645): the promenade. ──
  P('ship_floodlight', -22, 10, 400, 0.4, 4.2),
  P('ship_floodlight', 22, 10, 400, -0.4, 4.2),
  P('ship_floodlight', -18, 10, 500, 0.5, 4.2),
  P('ship_floodlight', 18, 10, 500, -0.5, 4.2),
  P('ship_lifeboat', -24, 10, 430, Math.PI / 2, 2.2),
  P('ship_lifeboat', 24, 10, 430, -Math.PI / 2, 2.2),
  P('ship_lifeboat', -20, 10, 480, Math.PI / 2, 2.2),
  P('ship_antenna', 0, 10, 632, 0, 3.4),
  P('ship_bell', 3, 10, 612, Math.PI, 1.2),
  P('ship_cargo_crate', -16, 10, 410, 0.7, 1.3),
  P('ship_barrel_stack', -13, 10, 414, 0, 1.1),
  P('ship_gametable', 10, 10, 455, 0.3, 0.95),
  P('ship_lounge_sofa', 13, 10, 462, -1.2, 0.95),
  P('ship_lounge_sofa', -10, 10, 540, 0.9, 0.95),
  P('station_bench', -8, 10, 580, 0.9, 0.9),
  P('station_bench', 6, 10, 600, -0.4, 0.9),
  P('ship_nav_globe', 0, 10, 560, 0, 1.6),
];

// ── Builder ────────────────────────────────────────────────────────────────

export function buildShipInterior(M: ShipMats): ShipInterior {
  const group = new THREE.Group();
  group.name = 'skyship-interior';
  const colliders: LocalBox[] = [];
  const glowMeshes: THREE.Mesh[] = [];

  const box = (mat: THREE.Material, x: number, y: number, z: number, sx: number, sy: number, sz: number, yaw = 0, collide = true, uvScale = 0): THREE.Mesh => {
    const g = new THREE.BoxGeometry(sx, sy, sz);
    if (uvScale > 0) {
      // Metre-scaled UVs on the big surfaces so the tiles read at world scale.
      const uv = g.attributes.uv as THREE.BufferAttribute;
      for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * sx / uvScale, uv.getY(i) * sz / uvScale);
    }
    const m = new THREE.Mesh(g, mat);
    m.position.set(x, y, z);
    m.rotation.y = yaw;
    m.receiveShadow = true;
    group.add(m);
    if (collide) colliders.push({ x, y, z, hx: sx / 2, hy: sy / 2, hz: sz / 2, yaw });
    return m;
  };

  const strip = (x: number, y: number, z: number, len: number, yaw: number): void => {
    const m = new THREE.Mesh(new THREE.PlaneGeometry(len, 0.22), M.glow);
    m.position.set(x, y, z);
    m.rotation.set(Math.PI / 2, 0, yaw + Math.PI / 2);
    group.add(m);
    glowMeshes.push(m);
  };

  // Stair openings (slab holes): we build slabs as strips around each hole.
  interface Hole { x: number; z: number; hx: number; hz: number }
  const slabWithHoles = (mat: THREE.Material, y: number, x0: number, x1: number, z0: number, z1: number, holes: Hole[], uv: number): void => {
    // Simple split: sort holes by z, emit z-strips; within a hole's z-band,
    // emit the two x-side pieces.
    const bands: Array<[number, number, Hole | null]> = [];
    let cur = z0;
    for (const h of [...holes].sort((a, b) => (a.z - a.hz) - (b.z - b.hz))) {
      const hz0 = Math.max(z0, h.z - h.hz), hz1 = Math.min(z1, h.z + h.hz);
      if (hz0 > cur) bands.push([cur, hz0, null]);
      bands.push([hz0, hz1, h]);
      cur = hz1;
    }
    if (cur < z1) bands.push([cur, z1, null]);
    for (const [bz0, bz1, h] of bands) {
      const bl = bz1 - bz0;
      if (bl < 0.05) continue;
      if (!h) {
        box(mat, (x0 + x1) / 2, y - FLOOR_T / 2, (bz0 + bz1) / 2, x1 - x0, FLOOR_T, bl, 0, true, uv);
      } else {
        const lx1 = h.x - h.hx, rx0 = h.x + h.hx;
        if (lx1 > x0 + 0.05) box(mat, (x0 + lx1) / 2, y - FLOOR_T / 2, (bz0 + bz1) / 2, lx1 - x0, FLOOR_T, bl, 0, true, uv);
        if (x1 > rx0 + 0.05) box(mat, (rx0 + x1) / 2, y - FLOOR_T / 2, (bz0 + bz1) / 2, x1 - rx0, FLOOR_T, bl, 0, true, uv);
      }
    }
  };

  // Stairwells: aft (eng↔crew↔officers) + forward (officers↔bridge) + cargo (eng↔crew).
  const stairs = (x: number, zBase: number, yLo: number, yHi: number, mat: THREE.Material): Hole => {
    const rise = yHi - yLo;
    const steps = Math.max(6, Math.round(rise / 0.28));
    const run = 2.6;
    for (let i = 0; i < steps; i++) {
      const t = (i + 0.5) / steps;
      box(mat, x, yLo + rise * t - 0.09, zBase + run * t, 1.6, 0.18, run / steps + 0.06);
    }
    // Handrail (visual only).
    box(M.gold, x + 0.85, yLo + rise / 2 + 0.9, zBase + run / 2, 0.06, 0.06, Math.hypot(run, rise) + 0.4, 0, false);
    return { x, z: zBase + run / 2 + 0.4, hx: 1.1, hz: run / 2 + 0.9 };
  };

  const aftHole = stairs(9, -34, DECKS.engineering.y, DECKS.crew.y, M.dark);
  const aftHole2 = stairs(9, -30, DECKS.crew.y, DECKS.officers.y, M.dark);
  const cargoHole = stairs(-9, 16, DECKS.engineering.y, DECKS.crew.y, M.dark);
  const bridgeHole = stairs(-6, 8, DECKS.officers.y, DECKS.bridge.y, M.dark);

  // ── Floors ──
  const E = DECKS.engineering, C = DECKS.crew, O = DECKS.officers, B = DECKS.bridge;
  box(M.grate, 0, E.y - FLOOR_T / 2, (E.z0 + E.z1) / 2, E.hw * 2, FLOOR_T, E.z1 - E.z0, 0, true, 3);
  slabWithHoles(M.deck, C.y, -C.hw, C.hw, C.z0, C.z1, [aftHole, cargoHole], 3);
  slabWithHoles(M.deck, O.y, -O.hw, O.hw, O.z0, O.z1, [aftHole2], 3);
  slabWithHoles(M.bridgeFloor, B.y, -B.hw, B.hw, B.z0, B.z1, [bridgeHole], 3);
  // Cargo floor patch (visual variety over the forward half of engineering).
  box(M.cargoFloor, 0, E.y - FLOOR_T / 2 + 0.02, 14, E.hw * 2 - 0.6, FLOOR_T, 22, 0, false, 3);

  // ── Ceilings ──
  // A deck's ceiling is the UNDERSIDE of the deck above. The old ceiling boxes
  // (y + CLEAR_H + FLOOR_T/2, FLOOR_T thick) occupied EXACTLY the volume of the
  // floor slab above them — engineering's ceiling was the crew-deck floor, the
  // officers' ceiling was the bridge floor — two textured top faces on one
  // plane: the flickering "floor textures overlap other textures" field report
  // (2026-09-03). Ceilings under a deck are now a thin sheet hung CEILING_GAP
  // below that slab's bottom (never coplanar, never inside it); only decks
  // with nothing above keep the free-standing ceiling.
  box(M.ceiling, 0, C.y - FLOOR_T - CEILING_GAP - CEILING_T / 2, (E.z0 + E.z1) / 2, E.hw * 2, CEILING_T, E.z1 - E.z0, 0, false, 3);
  box(M.ceiling, 0, B.y - FLOOR_T - CEILING_GAP - CEILING_T / 2, (O.z0 + O.z1) / 2, O.hw * 2, CEILING_T, O.z1 - O.z0, 0, false, 3);
  box(M.ceiling, 0, B.y + CLEAR_H + FLOOR_T / 2, (B.z0 + B.z1) / 2, B.hw * 2, FLOOR_T, B.z1 - B.z0, 0, false, 3);
  // Crew-deck ceiling only where officers' deck doesn't cover it.
  box(M.ceiling, 0, C.y + CLEAR_H + FLOOR_T / 2, (O.z1 + C.z1) / 2, C.hw * 2, FLOOR_T, C.z1 - O.z1, 0, false, 3);
  box(M.ceiling, 0, C.y + CLEAR_H + FLOOR_T / 2, (C.z0 + O.z0) / 2, C.hw * 2, FLOOR_T, O.z0 - C.z0, 0, false, 3);

  // ── Outer walls per deck (with window bands port/starboard) ──
  const outerWalls = (d: { y: number; z0: number; z1: number; hw: number }, windows: boolean, mat: THREE.Material, foreDoorHalf = 0, aftDoorHalf = 0): void => {
    const len = d.z1 - d.z0, zc = (d.z0 + d.z1) / 2;
    const h = CLEAR_H;
    for (const sgn of [-1, 1]) {
      if (windows) {
        // Sill + header + glass band between (the city shows through).
        box(mat, sgn * d.hw, d.y + 0.55, zc, WALL_T, 1.1, len, 0, true, 3);
        box(mat, sgn * d.hw, d.y + h - 0.65, zc, WALL_T, 1.3, len, 0, true, 3);
        const glass = box(M.glass, sgn * d.hw, d.y + 1.65, zc, WALL_T * 0.4, 1.1, len, 0, true, 0);
        glass.renderOrder = 3;
      } else {
        box(mat, sgn * d.hw, d.y + h / 2, zc, WALL_T, h, len, 0, true, 3);
      }
    }
    // End caps carry DOORS when asked: the crew deck opens aft onto the
    // concourse and forward onto the gallery through real gaps + lintels.
    if (aftDoorHalf > 0) {
      const flankW = d.hw - aftDoorHalf;
      for (const sgn of [-1, 1]) box(mat, sgn * (aftDoorHalf + flankW / 2), d.y + h / 2, d.z0, flankW, h, WALL_T, 0, true, 3);
      box(mat, 0, d.y + h - 0.4, d.z0, aftDoorHalf * 2, 0.8, WALL_T, 0, true, 3);
    } else {
      box(mat, 0, d.y + h / 2, d.z0, d.hw * 2, h, WALL_T, 0, true, 3);
    }
    if (foreDoorHalf > 0) {
      const flankW = d.hw - foreDoorHalf;
      for (const sgn of [-1, 1]) {
        box(mat, sgn * (foreDoorHalf + flankW / 2), d.y + h / 2, d.z1, flankW, h, WALL_T, 0, true, 3);
      }
      box(mat, 0, d.y + h - 0.4, d.z1, foreDoorHalf * 2, 0.8, WALL_T, 0, true, 3); // lintel
      box(M.gold, -foreDoorHalf, d.y + (h - 0.8) / 2, d.z1, 0.18, h - 0.8, WALL_T + 0.12, 0, false);
      box(M.gold, foreDoorHalf, d.y + (h - 0.8) / 2, d.z1, 0.18, h - 0.8, WALL_T + 0.12, 0, false);
    } else {
      box(mat, 0, d.y + h / 2, d.z1, d.hw * 2, h, WALL_T, 0, true, 3);
    }
  };
  outerWalls(E, false, M.wall);
  outerWalls(C, true, M.wall, 1.8, 2.2); // bow doors → the gallery; aft doors → the concourse
  outerWalls(O, true, M.quartersWall);
  // Bridge: solid sides, a wide GLASS bow viewport.
  for (const sgn of [-1, 1]) box(M.wall, sgn * B.hw, B.y + CLEAR_H / 2, (B.z0 + B.z1) / 2, WALL_T, CLEAR_H, B.z1 - B.z0, 0, true, 3);
  box(M.wall, 0, B.y + CLEAR_H / 2, B.z0, B.hw * 2, CLEAR_H, WALL_T, 0, true, 3);
  box(M.wall, 0, B.y + 0.5, B.z1, B.hw * 2, 1.0, WALL_T, 0, true, 3);
  const viewport = box(M.glass, 0, B.y + 0.9 + (CLEAR_H - 0.9) / 2, B.z1, B.hw * 2 - 0.8, CLEAR_H - 0.9, WALL_T * 0.4, 0, true, 0);
  viewport.renderOrder = 3;

  // ── Interior room walls (doors = 1.5 m gaps) ──
  const wallSeg = (mat: THREE.Material, x0: number, z0: number, x1: number, z1: number, y: number): void => {
    const len = Math.hypot(x1 - x0, z1 - z0);
    if (len < 0.1) return;
    const yaw = Math.atan2(x1 - x0, z1 - z0);
    box(mat, (x0 + x1) / 2, y + CLEAR_H / 2, (z0 + z1) / 2, WALL_T, CLEAR_H, len, yaw, true, 3);
  };
  // D3: berthing partition (x +4.5, z −18..−1) with door at z −1.5 gap.
  wallSeg(M.quartersWall, 4.5, -18, 4.5, -2.5, C.y);
  // D3: galley/mess partial screen.
  wallSeg(M.wall, -4.5, 12, -C.hw, 12, C.y);
  // D3: lounge threshold columns (visual).
  box(M.gold, -2.2, C.y + CLEAR_H / 2, 16, 0.25, CLEAR_H, 0.25);
  box(M.gold, 2.2, C.y + CLEAR_H / 2, 16, 0.25, CLEAR_H, 0.25);
  // D2 officers: captain's cabin aft-port (walls at z 4 / x −3.5 door gap).
  wallSeg(M.quartersWall, -O.hw, 4, -3.6, 4, O.y);
  wallSeg(M.quartersWall, -2.1, 4, 0, 4, O.y);
  wallSeg(M.quartersWall, 0, 4, 0, 14, O.y);
  // D2: medbay partition (x +3, z −18..−2, door gap at −3.5..−2).
  wallSeg(M.wall, 3, -18, 3, -4.9, O.y);
  wallSeg(M.wall, 3, -3.4, 3, -2, O.y);
  // D4: engine room bulkhead (z −16) with big door.
  wallSeg(M.wall, -E.hw, -16, -1.8, -16, E.y);
  wallSeg(M.wall, 1.8, -16, E.hw, -16, E.y);

  // ── The ARRIVAL CONCOURSE: the hangar hall the lift lands you in. ──
  {
    const K = CONCOURSE;
    const len = K.z1 - K.z0, zc = (K.z0 + K.z1) / 2;
    box(M.cargoFloor, 0, K.y - FLOOR_T / 2, zc, K.hw * 2, FLOOR_T, len, 0, true, 3);
    box(M.ceiling, 0, K.y + K.clearH + FLOOR_T / 2, zc, K.hw * 2, FLOOR_T, len, 0, false, 3);
    // Side walls: sill + tall glass band + header (the city under the hull).
    for (const sgn of [-1, 1]) {
      box(M.wall, sgn * K.hw, K.y + 0.6, zc, WALL_T, 1.2, len, 0, true, 3);
      const g = box(M.glass, sgn * K.hw, K.y + 1.2 + 5.5, zc, WALL_T * 0.4, 11, len, 0, true, 0);
      g.renderOrder = 3;
      box(M.wall, sgn * K.hw, K.y + 12.2 + (K.clearH - 12.2) / 2, zc, WALL_T, K.clearH - 12.2, len, 0, true, 3);
    }
    // Aft cap (the airlock wall) + the door into the room stack forward.
    box(M.wall, 0, K.y + K.clearH / 2, K.z0, K.hw * 2, K.clearH, WALL_T, 0, true, 3);
    for (const sgn of [-1, 1]) box(M.wall, sgn * (2.2 + (K.hw - 2.2) / 2), K.y + K.clearH / 2, K.z1, K.hw - 2.2, K.clearH, WALL_T, 0, true, 3);
    box(M.wall, 0, K.y + K.clearH - (K.clearH - 3.2) / 2, K.z1, 4.4, K.clearH - 3.2, WALL_T, 0, true, 3);
    // Pillar rows + light strips down the hall.
    for (let z = K.z0 + 26; z < K.z1 - 10; z += 26) {
      for (const sgn of [-1, 1]) box(M.dark, sgn * 30, K.y + K.clearH / 2, z, 1.6, K.clearH, 1.6);
      strip(-15, K.y + K.clearH - 0.06, z, 24, Math.PI / 2);
      strip(15, K.y + K.clearH - 0.06, z, 24, Math.PI / 2);
    }
    strip(0, K.y + K.clearH - 0.06, zc, len - 8, 0);
    // The airlock alcove: a lit threshold where the lift lands.
    box(M.gold, 0, K.y + INLAY_LIFT, ARRIVAL.z, 6, 0.02, 6, 0, false);
    box(M.gold, -3.2, K.y + 1.4, K.z0 + 1, 0.25, 2.8, 0.25);
    box(M.gold, 3.2, K.y + 1.4, K.z0 + 1, 0.25, 2.8, 0.25);
  }

  // ── The SPINE GALLERY: the windowed walk to the prow. ──
  {
    const G = GALLERY;
    const len = G.z1 - G.z0, zc = (G.z0 + G.z1) / 2;
    box(M.deck, 0, G.y - FLOOR_T / 2, zc, G.hw * 2, FLOOR_T, len, 0, true, 3);
    box(M.ceiling, 0, G.y + CLEAR_H + FLOOR_T / 2, zc, G.hw * 2, FLOOR_T, len, 0, false, 3);
    for (const sgn of [-1, 1]) {
      box(M.wall, sgn * G.hw, G.y + 0.5, zc, WALL_T, 1.0, len, 0, true, 3);
      const g = box(M.glass, sgn * G.hw, G.y + 1.0 + 1.4, zc, WALL_T * 0.4, 2.8, len, 0, true, 0);
      g.renderOrder = 3;
    }
    strip(0, G.y + CLEAR_H - 0.06, zc, len - 6, 0);
    // Gallery bulkhead ribs every 35 m (the hull's frames, read from inside).
    for (let z = G.z0 + 35; z < G.z1 - 10; z += 35) {
      for (const sgn of [-1, 1]) box(M.gold, sgn * (G.hw - 0.3), G.y + CLEAR_H / 2, z, 0.3, CLEAR_H, 0.6, 0, false);
    }
  }

  // ── The FOREDECK: tapered planks, perimeter railing, hull skirt. ──
  {
    const segs = 16;
    for (let i = 0; i < segs; i++) {
      const z0 = FOREDECK.z0 + (FOREDECK.z1 - FOREDECK.z0) * (i / segs);
      const z1 = FOREDECK.z0 + (FOREDECK.z1 - FOREDECK.z0) * ((i + 1) / segs);
      const hw = (foredeckHalfW(z0) + foredeckHalfW(z1)) / 2;
      box(M.foredeck, 0, FOREDECK.y - FLOOR_T / 2, (z0 + z1) / 2, hw * 2, FLOOR_T, z1 - z0, 0, true, 3);
      // Hull skirt under the deck edge (reads as the ship's bow from aboard).
      box(M.hull, 0, FOREDECK.y - 2.2, (z0 + z1) / 2, hw * 2 + 1.6, 4, z1 - z0, 0, false, 6);
      // Railing: posts + 3 rails along each tapered edge (solid — the rail
      // holds; the sky-dive is a deliberate jump, not a stumble).
      for (const sgn of [-1, 1]) {
        const ex0 = sgn * foredeckHalfW(z0), ex1 = sgn * foredeckHalfW(z1);
        const midX = (ex0 + ex1) / 2, midZ = (z0 + z1) / 2;
        const len = Math.hypot(ex1 - ex0, z1 - z0);
        const yaw = Math.atan2(ex1 - ex0, z1 - z0);
        box(M.dark, midX, FOREDECK.y + 0.62, midZ, 0.12, 1.24, len + 0.1, yaw, true);
        box(M.gold, midX, FOREDECK.y + 1.28, midZ, 0.07, 0.07, len + 0.1, yaw, false);
      }
    }
    // Bow point cap rail.
    box(M.dark, 0, FOREDECK.y + 0.62, FOREDECK.z1, FOREDECK.hw1 * 2 + 0.3, 1.24, 0.12, 0, true);
    box(M.gold, 0, FOREDECK.y + 1.28, FOREDECK.z1, FOREDECK.hw1 * 2 + 0.3, 0.07, 0.07, 0, false);
    // Gold centerline inlay — the processional line of the ship itself.
    box(M.gold, 0, FOREDECK.y + INLAY_LIFT, (FOREDECK.z0 + FOREDECK.z1) / 2, 0.5, 0.02, FOREDECK.z1 - FOREDECK.z0, 0, false);
    // Bow doors from the gallery onto the deck: a lintel + gold frames.
    box(M.wall, 0, FOREDECK.y + CLEAR_H - 0.4, FOREDECK.z0, GALLERY.hw * 2, 0.8, WALL_T, 0, true, 3);
    for (const sgn of [-1, 1]) box(M.gold, sgn * (GALLERY.hw - 0.2), FOREDECK.y + (CLEAR_H - 0.8) / 2, FOREDECK.z0, 0.3, CLEAR_H - 0.8, 0.6, 0, false);
  }

  // ── Ceiling light strips (warm) down every corridor spine ──
  strip(0, C.y + CLEAR_H - 0.06, -10, 52, 0);
  strip(0, O.y + CLEAR_H - 0.06, -8, 40, 0);
  strip(0, E.y + CLEAR_H - 0.06, -7, 60, 0);
  strip(0, B.y + CLEAR_H - 0.06, 18, 22, 0);
  strip(-6.5, C.y + CLEAR_H - 0.06, 5, 28, 0);
  strip(6.5, C.y + CLEAR_H - 0.06, -8, 24, 0);

  // (No fake hull shell: the interior sits INSIDE the real civa_city_hall
  //  mesh — its walls are the ship's walls.)

  return { group, colliders, glowMeshes };
}
